import { useState, useMemo } from 'react';

// Utils
import groupOptionsByOptGroup from '../../utils/optGroup/optGroup';

// Use cases
import { addIdsToEditFormUrls, filterEditFormUrlsBySearch } from './dashboardEditFormUseCase';

/**
 * @typedef {object} useDashboardEditFormReturn
 * @property {boolean} popoverEditFormIsOpen - Whether the edit form popover is open
 * @property {(isOpen: boolean) => void} setPopoverEditFormIsOpen - Set the edit form popover open state
 * @property {string} searchValue - The current search value
 * @property {(value: string) => void} setSearchValue - Set the search value
 * @property {Array<{lib: string, options: Array<DashboardEditFormUrlsWithId>}>} editFormUrlsGrouped - The edit form URLs grouped by optGroup and filtered by search
 */

/**
 * Hook to manage dashboard edit form functionality
 * @param {object} props
 * @param {Array<ApiDashboardEditFormUrls>} [props.editFormUrls] - The edit form URLs from the API
 * @returns {useDashboardEditFormReturn} Edit form state and actions
 */
const useDashboardEditForm = ({ editFormUrls = [] }) => {
  const [popoverEditFormIsOpen, setPopoverEditFormIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  /**
   * @description The edit form URLs grouped by optGroup and filtered by search
   * @type {Array<{lib: string, options: Array<DashboardEditFormUrlsWithId>}>}
   */
  const editFormUrlsGrouped = useMemo(() => {
    // Add IDs to items
    const withIds = addIdsToEditFormUrls(editFormUrls);

    // Filter by search value
    const filtered = filterEditFormUrlsBySearch(withIds, searchValue);

    // Group by optGroup
    return groupOptionsByOptGroup(filtered);
  }, [editFormUrls, searchValue]);

  return {
    // Popover state
    popoverEditFormIsOpen,
    setPopoverEditFormIsOpen,

    // Search
    searchValue,
    setSearchValue,

    // Data
    editFormUrlsGrouped,
  };
};

export default useDashboardEditForm;
